import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Configure axios defaults
  useEffect(() => {
    if (token && token.trim() && token !== 'null' && token !== 'undefined') {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token && token.trim() && token !== 'null' && token !== 'undefined') {
        try {
          const response = await api.get('/api/auth/me');
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      } else {
        // Clear invalid token
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login...', { email });
      console.log('📡 API base URL:', api.defaults.baseURL);
      
      const response = await api.post('/api/auth/login', {
        email,
        password
      });

      console.log('✅ Login response received:', response.status);
      
      const { token: newToken, user: userData } = response.data;
      
      console.log('💾 Attempting to save token to localStorage...');
      try {
        localStorage.setItem('token', newToken);
        const savedToken = localStorage.getItem('token');
        console.log('✅ Token saved:', savedToken ? 'Yes' : 'No');
      } catch (storageError) {
        console.error('❌ localStorage error:', storageError);
        toast.error('Cannot save login session. Are you in Private Browsing mode?');
        return { success: false, error: 'localStorage not available' };
      }
      
      setToken(newToken);
      setUser(userData);
      
      console.log('✅ Login successful!', { user: userData.email });
      toast.success(`Welcome back, ${userData.first_name || userData.name || userData.email}!`);
      
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      // Expect: { email, first_name, last_name, user_type? }
      const payload = {
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        user_type: userData.user_type || 'personal'
      };
      const response = await api.post('/api/auth/register', payload);

      // Backend returns 201 with message and user (no token; activation required)
      const successMessage =
        response.data?.message ||
        'Welcome to BoatWise! Please check your email to activate your account.';
      toast.success(successMessage);

      return { success: true, message: successMessage };
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Registration failed';
      // Surface validation details if present
      const details = error.response?.data?.details;
      if (Array.isArray(details) && details.length) {
        details.forEach((d) => {
          if (d?.msg) toast.error(d.msg);
        });
      } else {
        toast.error(message);
      }
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      console.log('Attempting logout...');
      const response = await api.post('/api/auth/logout');
      console.log('Logout response:', response.data);
    } catch (error) {
      console.error('Logout request failed:', error);
      // Still proceed with logout even if the request fails
    } finally {
      console.log('Clearing local storage and state...');
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      toast.success('Successfully logged out');
    }
  };

  const clearInvalidToken = () => {
    console.log('Clearing invalid token...');
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/api/auth/profile', profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully');
      return { success: true, user: response.data.user };
    } catch (error) {
      const message = error.response?.data?.error
        || error.response?.data?.details?.[0]?.msg
        || error.response?.data?.message
        || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/api/auth/password', {
        currentPassword,
        newPassword
      });
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearInvalidToken,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
